import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  useEventCallback,
} from '@mui/material';
import React, { memo, useCallback, useEffect } from 'react';
import MunicipalityAutoComplete from 'system/components/shared/search-municipality';
import PmiTextField from 'shell/components/shared/text-field';
import MunicipalityList, { MunicipalityAnprVM } from 'shell/components/shared/municipality-list';
import ForeignStateList, { ForeignStateVM } from 'shell/components/shared/foreign-state-list';
import MunicipalityForeignList from 'shell/components/shared/municipality-foreign-list';
import { noop } from 'shell/utils/noop';
import { DateTime } from 'luxon';
import { TypeRegister } from 'practice/constants/type-register';
import { ResidenceOrigin } from 'shell/view-models/personal-data';
import { DisabledFields } from 'shell/view-models/disabled-fields';

interface ResidenceOriginFormProps {
  onChange: (key: keyof ResidenceOrigin, value: ResidenceOrigin[keyof ResidenceOrigin]) => void;
  onChangeValues: (values: ResidenceOrigin) => void;
  values?: ResidenceOrigin;
  label?: string;
  typeRegister?: string;
  disabled?: boolean;
  disabledFields?: DisabledFields<ResidenceOrigin>;
  required?: boolean;
  fromDate?: DateTime | null;
}

function ResidenceOriginForm({
  onChange,
  values,
  disabled,
  typeRegister,
  required,
  onChangeValues,
  fromDate,
  disabledFields,
}: ResidenceOriginFormProps): React.ReactElement {
  const handleMunicipalityListChange = useEventCallback(
    (municipality: MunicipalityAnprVM | null) => {
      onChange('origin', municipality);
    }
  );

  const handleForeignStateListChange = useEventCallback((foreignState?: ForeignStateVM | null) => {
    onChangeValues({ originState: foreignState, originMunicipalityForeign: null });
  });

  const handleInputKeyboardChange = useEventCallback((municipality: MunicipalityAnprVM | null) => {
    onChange('originMunicipalityForeign', municipality);
  });

  const resetForeign = useCallback(() => {
    onChangeValues({ originState: null, originMunicipalityForeign: null });
  }, [onChangeValues]);

  const resetItalian = useCallback(() => {
    onChangeValues({
      origin: null,
    });
  }, [onChangeValues]);

  useEffect(() => {
    if (!values?.neverRegistered) {
      return;
    }

    // se è true viene resettato
    onChangeValues({
      isForeign: false,
      origin: null,
      originState: null,
      originMunicipalityForeign: null,
      previousOrigin: undefined,
      previousOriginProvince: undefined,
      previousOriginSipoCode: undefined,
      previousOriginCode: undefined,
    });
  }, [values?.neverRegistered]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="Provenienza"
            id="isForeign"
            value={values?.isForeign || 'false'}
            onChange={() => {
              onChange('isForeign', !values?.isForeign);
            }}
          >
            <FormControlLabel
              onClick={() => {
                resetItalian();
              }}
              value="false"
              control={<Radio />}
              label="Italia"
              disabled={values?.neverRegistered}
            />
            <FormControlLabel
              onClick={() => {
                resetForeign();
              }}
              value="true"
              control={<Radio />}
              label="Estero"
              disabled={values?.neverRegistered}
            />
          </RadioGroup>
        </FormControl>
      </Grid>
      {!values?.isForeign ? (
        <Grid item xs={12} md={9} container spacing={2}>
          <Grid item xs={12} md={8}>
            <MunicipalityList
              value={values?.origin || null}
              onChange={handleMunicipalityListChange}
              label={`Comune ${required ? '*' : ''}`}
              fromDate={fromDate}
              disabled={values?.neverRegistered}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              aria-label="Provincia"
              label="Provincia"
              id="province"
              name="province"
              fullWidth
              disabled
              type="text"
              variant="outlined"
              value={values?.origin?.province || ''}
              onChange={noop}
            />
          </Grid>
        </Grid>
      ) : (
        <Grid item xs={12} md={9} container spacing={2}>
          <Grid item xs={12} md={6}>
            <ForeignStateList
              value={values?.originState || null}
              onChange={handleForeignStateListChange}
              label={`Stato Estero ${required ? '*' : ''}`}
              id="foreignState"
              disabled={values?.neverRegistered}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <MunicipalityForeignList
              value={values?.originMunicipalityForeign || null}
              onChange={handleInputKeyboardChange}
              label="Comune Estero"
              id="foreignMunicipality"
              disabled={!values?.originState?.id || values?.neverRegistered}
              foreignStateIstatCode={values?.originState?.id}
            />
          </Grid>
        </Grid>
      )}
      <Grid item xs={12} md={9} container spacing={2}>
        <Grid item xs={12} md={3} display="flex" alignItems="center">
          <FormControlLabel
            control={
              <Checkbox
                id="irreperibile"
                checked={values?.irreperibile || false}
                onChange={() => {
                  onChangeValues({
                    irreperibile: !values?.irreperibile,
                    originState: !values?.irreperibile ? undefined : null,
                    originMunicipalityForeign: !values?.irreperibile ? undefined : null,
                    origin: !values?.irreperibile ? undefined : null,
                  });
                }}
                inputProps={{ 'aria-label': 'Irreperibile' }}
              />
            }
            label="Irreperibile"
          />
        </Grid>
        {typeRegister === TypeRegister.Immigration ? (
          <Grid item xs={12} md={4} display="flex" alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  id="neverRegistered"
                  checked={values?.neverRegistered || false}
                  onChange={() => onChange('neverRegistered', !values?.neverRegistered)}
                  inputProps={{ 'aria-label': 'Mai anagrafato' }}
                />
              }
              label="Mai anagrafato"
            />
          </Grid>
        ) : null}
      </Grid>
      <Grid item xs={12} md={6}>
        <MunicipalityAutoComplete
          selectedValue={{ description: values?.previousOrigin ? values.previousOrigin : '' }}
          handleChange={(value) =>
            onChangeValues({
              previousOrigin: value?.description,
              previousOriginProvince: value?.province,
              previousOriginSipoCode: value?.code,
            })
          }
          label="Comune precedente iscrizione"
          params={{
            italia: false,
            irreperibile: true,
          }}
          error={false}
          disabled={disabled || values?.neverRegistered || disabledFields?.previousOrigin || false}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <PmiTextField
          aria-label="Provincia precedente iscrizione"
          label="Provincia precedente iscrizione"
          id="previousOriginProvince"
          name="previousOriginProvince"
          fullWidth
          disabled
          type="text"
          variant="outlined"
          value={values?.previousOriginProvince || ''}
          onChange={(event) => {
            onChange('previousOriginProvince', event.target.value);
          }}
        />
      </Grid>
    </Grid>
  );
}

ResidenceOriginForm.displayName = 'Residence Origin Form Component';

export default memo(ResidenceOriginForm);
